import { formatDateString } from './dateUtils';
export const formatPatientsForGrid = (data, userDateFormat) => {
    const formattedEnrollments = {};
    const uniqueIds = [];
    const enrollmentsByPatientId = {};

    // Iterate through each enrollment
    data.forEach(enrollment => {
        const { patient, organizationId, groupId, groupName, enrollment: { status, groups, dischargeDate, startDate, endDate }, name, duration, enrollmentId, devices } = enrollment;
        const statusName = dischargeDate ? "Previously Enrolled" : status;
        const key = `${patient.id}_${statusName}`;
        if (!uniqueIds.includes(patient.id)) {
            uniqueIds.push(patient.id);
            enrollmentsByPatientId[patient.id] = { "previous": [], "current": [] };
        }

        // Create or update the formatted enrollment object for this patient ID and status
        if (!formattedEnrollments[key]) {
            formattedEnrollments[key] = {
                id: key,
                patientId: patient.id,
                status: statusName,
                name: `${patient.firstName} ${patient.lastName}`,
                profilePictureURL: patient.profilePictureURL,
                firstName: patient.firstName,
                lastName: patient.lastName,
                groupId: groupId,
                groupName: groupName,
                organizationId: organizationId,
                programNames: [],
                programNamesDisplay: '',
                durations: [],
                durationsDisplay: '',
                datesEnrolled: [],
                datesEnrolledDisplay: '',
                expectedDischargeDates: [],
                expectedDischargeDatesDisplay: '',
                datesDischarged: [],
                datesDischargedDisplay: '',
                selectOptions: [],
                data: [],
                organizationIds: [organizationId],
                groupIds: [],
                subGroupIds: [],
                teamIds: [],
                previouslyEnrolledSelection: [],
            };
        }

        groups.forEach(group => {
            if ("groupId" in group && group.groupId && !formattedEnrollments[key].groupIds.includes(group.groupId)) {
                formattedEnrollments[key].groupIds.push(group.groupId);
            }
            if ("subGroupId" in group && group.subGroupId && !formattedEnrollments[key].subGroupIds.includes(group.subGroupId)) {
                formattedEnrollments[key].subGroupIds.push(group.subGroupId);
            }
            if ("teamId" in group && group.subGroupId && !formattedEnrollments[key].teamIds.includes(group.teamId)) {
                formattedEnrollments[key].teamIds.push(group.teamId);
            }
        });


        // Add program names, durations, and dates enrolled with formatted dates
        formattedEnrollments[key].programNames.push(name);
        formattedEnrollments[key].durations.push(duration);
        formattedEnrollments[key].datesEnrolled.push(formatDate(startDate, userDateFormat));
        formattedEnrollments[key].expectedDischargeDates.push(formatDate(endDate, userDateFormat));

        if (dischargeDate) {
            formattedEnrollments[key].datesDischarged.push(formatDate(dischargeDate, userDateFormat));
            let updatedPreviousEnrollments = [...enrollmentsByPatientId[patient.id]["previous"]];
            enrollmentsByPatientId[patient.id]["previous"] = [...updatedPreviousEnrollments, name];

            formattedEnrollments[key].previouslyEnrolledSelection.push({ name: `${name} (Discharged ${dischargeDate})`, id: enrollmentId });
        };

        if (!dischargeDate) {
            let updatedCurrentEnrollments = [...enrollmentsByPatientId[patient.id]["current"]];
            enrollmentsByPatientId[patient.id]["current"] = [...updatedCurrentEnrollments, name];
        }

        // use this for selecting programs to remove + update
        formattedEnrollments[key].selectOptions.push({ name: name, id: enrollmentId });

        // Add original enrollment data
        formattedEnrollments[key].data.push(enrollment);
    });
    // Return an array of the formatted enrollment objects
    uniqueIds.forEach((id) => {
        let previous = false;
        let active = false;
        if (formattedEnrollments[`${id}_Previously Enrolled`]) {
            previous = true;
        }
        if (formattedEnrollments[`${id}_Enrolled`]) {
            active = true;
        }

        // save previous enrollments to user for later selection
        if (active && previous) {
            formattedEnrollments[`${id}_Enrolled`].previouslyEnrolledSelection = formattedEnrollments[`${id}_Previously Enrolled`].previouslyEnrolledSelection;
            formattedEnrollments[`${id}_Enrolled`].data = formattedEnrollments[`${id}_Enrolled`].data.concat(formattedEnrollments[`${id}_Previously Enrolled`].data);
        }
    });
    
    const formattedEnrollmentsArray = Object.values(formattedEnrollments);
    return {formattedEnrollmentsArray, enrollmentsByPatientId};
}

/**
 * Cached modality values are scoped to the Perin Patch (deviceId === 1). A patient may also be
 * enrolled with third-party devices that share modalityIds with the Patch (e.g. a BP cuff exposes
 * pulse rate as modalityId 3, same as Patch pulse oximetry). Caching those side-by-side under a
 * single modalityId let whichever enrollment iterated first silently overwrite the Patch values,
 * so the Patch UI defaulted to the cuff's frequency/interval. Third-party device serial numbers
 * are still cached per-device so they can be reused without conflicting.
 */
const PERIN_PATCH_DEVICE_ID = 1;

export const savePatientEnrollmentData = (data) => {

    const currentModalities = {};
    const currentSerialNumbers = {};

    data.forEach(enrollment => {
        const { patient, enrollment: { dischargeDate }, devices = [] } = enrollment;

        if (!dischargeDate) {
            if (!currentModalities[patient.id]) {
                currentModalities[patient.id] = {};
            };
            if (!currentSerialNumbers[patient.id]) {
                currentSerialNumbers[patient.id] = {};
            };

            const patchModalitiesById = {};
            devices.forEach(deviceModality => {
                if (deviceModality.deviceId !== PERIN_PATCH_DEVICE_ID) return;
                if (!patchModalitiesById[deviceModality.modalityId]) {
                    patchModalitiesById[deviceModality.modalityId] = [];
                }
                patchModalitiesById[deviceModality.modalityId].push(deviceModality);
            });

            Object.keys(patchModalitiesById).forEach(modalityId => {
                const patchDevice = patchModalitiesById[modalityId][0];
                if (!currentModalities[patient.id].hasOwnProperty(modalityId)) {
                    currentModalities[patient.id][modalityId] = {
                        upperLimit: patchDevice.upperLimit,
                        lowerLimit: patchDevice.lowerLimit,
                        targetValue: patchDevice.targetValue,
                        isContinuous: patchDevice.isContinuous,
                        startTime: patchDevice.startTime,
                        frequency: patchDevice.frequency,
                        interval: patchDevice.interval,
                    };
                }
            });

            devices.forEach(deviceModality => {
                if (!currentSerialNumbers[patient.id].hasOwnProperty(deviceModality.deviceId)) {
                    currentSerialNumbers[patient.id][deviceModality.deviceId] = {
                        serialNumber: deviceModality.serialNumber,
                        serialNumberId: deviceModality.serialNumberId
                    };
                }
            });
        }

    });

    return { currentModalities, currentSerialNumbers };
}

const formatDate = (dateStr, userDateFormat) => {
    if (!dateStr) return '';
    try {
        // Use the dateStr directly instead of creating new Date to avoid timezone conversion issues
        return formatDateString(dateStr, userDateFormat).replace(/\//g, '-');
    } catch (error) {
        console.error('Error formatting date:', error);
        return dateStr;
    }
};